import { useId, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, Info } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { BrandMark } from '@/components/layout/Brand';
import { Button, Checkbox, FormField, Input, Modal, fieldDescribedBy } from '@/components/ui';
import { ApiError } from '@/lib/api';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function signInErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'INVALID_CREDENTIALS') return 'The email address or password is incorrect.';
    if (err.code === 'ACCOUNT_INACTIVE') return 'This account is inactive. Contact a Super Admin to restore access.';
    if (err.code === 'RATE_LIMITED') return 'Too many sign-in attempts. Please wait a few minutes and try again.';
    if (err.code === 'NETWORK_ERROR') return 'The admin service could not be reached. Please try again shortly.';
  }
  return 'Sign in failed because of a server error. Please try again.';
}

export function LoginPage() {
  const { user, sessionExpired, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const emailId = useId();
  const passwordId = useId();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? '/categories';
  if (user) return <Navigate to={from} replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!email.trim()) next.email = 'Enter your email address.';
    else if (!EMAIL_PATTERN.test(email.trim())) next.email = 'Enter a valid email address.';
    if (!password) next.password = 'Enter your password.';
    setErrors(next);
    setFormError(null);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      await signIn(email, password, remember);
      navigate(from, { replace: true });
    } catch (err) {
      setFormError(signInErrorMessage(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-page px-4 py-10">
      <main className="w-full max-w-[400px]">
        <div className="rounded-xl border border-line bg-surface px-8 py-9 shadow-card">
          <div className="mb-7 flex flex-col items-center text-center">
            <BrandMark size={44} />
            <p className="mt-4 text-xs font-bold uppercase tracking-[0.18em] text-ink">Quds Technology</p>
            <h1 className="mt-1 text-xl font-semibold tracking-tight text-ink">Central Admin</h1>
            <p className="mt-1.5 text-[13px] text-ink-secondary">Sign in with your staff account</p>
          </div>

          {sessionExpired && !formError && (
            <div role="status" className="mb-5 flex items-start gap-2 rounded-md border border-primary/20 bg-primary-soft px-3 py-2.5 text-[13px] text-primary">
              <Info size={16} aria-hidden="true" className="mt-px shrink-0" />
              Your session has ended. Please sign in again.
            </div>
          )}

          {formError && (
            <div role="alert" className="mb-5 flex items-start gap-2 rounded-md border border-danger/20 bg-danger-soft px-3 py-2.5 text-[13px] text-danger">
              <AlertCircle size={16} aria-hidden="true" className="mt-px shrink-0" />
              {formError}
            </div>
          )}

          <form noValidate onSubmit={onSubmit} className="space-y-4">
            <FormField label="Email Address" htmlFor={emailId} error={errors.email}>
              <Input
                id={emailId}
                type="email"
                autoComplete="username"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                invalid={!!errors.email}
                aria-describedby={fieldDescribedBy(emailId, { error: errors.email })}
                autoFocus
              />
            </FormField>

            <FormField
              label="Password"
              htmlFor={passwordId}
              error={errors.password}
              labelAside={
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  className="rounded text-[13px] font-medium text-primary hover:underline focus-visible:shadow-focus focus-visible:outline-none"
                >
                  Forgot password?
                </button>
              }
            >
              <div className="relative">
                <Input
                  id={passwordId}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  invalid={!!errors.password}
                  aria-describedby={fieldDescribedBy(passwordId, { error: errors.password })}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded text-ink-muted hover:bg-surface-sunken hover:text-ink focus-visible:shadow-focus focus-visible:outline-none"
                >
                  {showPassword ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                </button>
              </div>
            </FormField>

            <Checkbox label="Remember me" checked={remember} onChange={(e) => setRemember(e.target.checked)} />

            <Button type="submit" size="lg" className="w-full" loading={submitting}>
              {submitting ? 'Signing in…' : 'Sign In'}
            </Button>
          </form>
        </div>

      </main>

      <Modal
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        title="Password recovery"
        size="sm"
        footer={
          <Button variant="secondary" onClick={() => setForgotOpen(false)}>
            Close
          </Button>
        }
      >
        <p className="text-sm leading-relaxed text-ink-secondary">
          Self-service password recovery is not available. Ask a Super Admin to reset your password; you will choose a new one when you next sign in to your
          Central Admin account.
        </p>
      </Modal>
    </div>
  );
}
